import React, { useEffect, useRef, useState } from 'react';

const SEGMENTS = 20;
const SHIFT_FROM = 13; // amber "shift" zone starts here
const REDLINE_FROM = 17; // red zone starts here
const TICK_MS = 120;

const COLOR_REV = 'var(--color-success)';
const COLOR_SHIFT = '#ffb547';
const COLOR_REDLINE = 'var(--color-danger)';

function segmentColor(i) {
  if (i >= REDLINE_FROM) return COLOR_REDLINE;
  if (i >= SHIFT_FROM) return COLOR_SHIFT;
  return COLOR_REV;
}

// Inject keyframe animation via style tag
const revStyle = `
@keyframes rvRevPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
@media (prefers-reduced-motion: reduce) { .rv-rev-lead { animation: none !important; } }
`;

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: 40,
    textAlign: 'center',
  },
  wrapCompact: {
    gap: 6,
    padding: 12,
  },
  percent: {
    fontFamily: 'var(--font-heading)',
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--color-text)',
    letterSpacing: 1,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  percentCompact: {
    fontSize: 16,
  },
  bar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 3,
    width: '100%',
    maxWidth: 420,
    height: 30,
  },
  barCompact: {
    maxWidth: 220,
    height: 16,
    gap: 2,
  },
  segment: {
    flex: 1,
    borderRadius: 2,
    transform: 'skewX(-14deg)',
    transition: 'background 120ms linear, box-shadow 120ms linear',
  },
  scale: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 420,
    fontFamily: 'var(--font-data)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
  },
  label: {
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: 'var(--color-secondary)',
  },
  detail: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
  },
};

/*
 * Rev-counter style loading indicator shared by every page.
 *
 * - done/total: completed requests out of the page's total. The bar never drops
 *   below done/total, so each finished request is a real jump.
 * - Within the request in flight the bar creeps toward the next mark on a time
 *   estimate (expectedMs is roughly how long that request usually takes), so the
 *   percentage keeps moving but never reaches 100 before the data arrives.
 * - Omit done/total for a single request.
 */
export default function LoadingProgress({ label = 'Loading', done = 0, total = 1, detail, expectedMs = 4000, compact = false }) {
  const steps = total > 0 ? total : 1;
  const completed = Math.min(Math.max(done, 0), steps);

  const startRef = useRef(Date.now());
  const stepStartRef = useRef(Date.now());
  const lastCompletedRef = useRef(completed);
  const maxPercentRef = useRef(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  if (completed !== lastCompletedRef.current) {
    // A restart (done went back down) begins a fresh run.
    if (completed < lastCompletedRef.current) {
      maxPercentRef.current = 0;
      startRef.current = now;
    }
    lastCompletedRef.current = completed;
    stepStartRef.current = now;
  }

  const stepElapsed = Math.max(0, now - stepStartRef.current);
  const creep = completed < steps ? 0.92 * (1 - Math.exp(-stepElapsed / expectedMs)) : 0;
  const raw = Math.min(99, ((completed + creep) / steps) * 100);
  maxPercentRef.current = Math.max(maxPercentRef.current, raw);
  const percent = Math.floor(maxPercentRef.current);

  const lit = Math.max(1, Math.round((percent / 100) * SEGMENTS));
  const elapsedSec = Math.floor((now - startRef.current) / 1000);

  return (
    <div
      style={{ ...styles.wrap, ...(compact ? styles.wrapCompact : {}) }}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      data-doc-id="loading-progress"
    >
      <style>{revStyle}</style>
      <div style={{ ...styles.percent, ...(compact ? styles.percentCompact : {}) }}>{percent}%</div>
      <div style={{ ...styles.bar, ...(compact ? styles.barCompact : {}) }}>
        {Array.from({ length: SEGMENTS }, (_, i) => {
          const on = i < lit;
          const color = segmentColor(i);
          return (
            <div
              key={i}
              className={on && i === lit - 1 ? 'rv-rev-lead' : undefined}
              style={{
                ...styles.segment,
                height: `${40 + (60 * i) / (SEGMENTS - 1)}%`,
                background: on ? color : 'var(--color-surface-light)',
                border: on ? `1px solid ${color}` : '1px solid var(--color-border)',
                boxShadow: on ? `0 0 8px ${color}` : 'none',
                animation: on && i === lit - 1 ? 'rvRevPulse 0.7s ease-in-out infinite' : 'none',
              }}
            />
          );
        })}
      </div>
      {!compact && (
        <div style={styles.scale} aria-hidden="true">
          {Array.from({ length: 11 }, (_, i) => (
            <span key={i} style={i >= 9 ? { color: COLOR_REDLINE } : undefined}>{i}</span>
          ))}
        </div>
      )}
      <div style={styles.label}>
        {label}
        {elapsedSec >= 3 ? ` · ${elapsedSec}s` : ''}
      </div>
      {detail && !compact && <div style={styles.detail}>{detail}</div>}
    </div>
  );
}
